import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Users, Trash2, Check, Crown, Plus, Leaf, ShieldAlert } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useProfiles } from '../contexts/ProfileContext';
import { sexOptions, ageOptions, symptomOptions, conditionOptions, conditionLabel } from '../data/profileOptions';
import { toast } from 'sonner';

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs transition-colors capitalize ${
        active ? 'bg-botanica-green text-white font-medium' : 'bg-botanica-card/60 text-botanica-muted hover:text-botanica-ink'
      }`}
    >
      {children}
    </button>
  );
}

export default function ProfilePage() {
  const { user, openAuth, setUser } = useAuth();
  const { profiles, create, remove, setActive, activeId, update } = useProfiles();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [sex, setSex] = useState('female');
  const [age, setAge] = useState('adult');
  const [symptoms, setSymptoms] = useState([]);
  const [extra, setExtra] = useState('');
  const [conditions, setConditions] = useState([]);
  const [saving, setSaving] = useState(false);

  // family builder
  const [familyName, setFamilyName] = useState('');
  const [familyMembers, setFamilyMembers] = useState([]);

  const isPremium = !!user?.is_premium;
  const individualCount = profiles.filter((p) => !p.is_family).length;
  const atFreeLimit = !isPremium && individualCount >= 1;

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-5 py-20 text-center min-h-[70vh]">
        <span className="w-14 h-14 rounded-2xl bg-botanica-greenLight flex items-center justify-center mx-auto mb-5">
          <User className="w-6 h-6 text-botanica-green" />
        </span>
        <h1 className="font-serif text-3xl text-botanica-ink mb-2">Sign in to create a profile</h1>
        <p className="text-botanica-muted mb-6">Profiles personalise your remedies and hide anything unsafe for you.</p>
        <button onClick={() => openAuth('register')} className="px-6 py-3 rounded-full bg-botanica-green text-white font-medium hover:bg-botanica-greenDark transition-colors">
          Create free account
        </button>
      </div>
    );
  }

  const toggle = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  const resetForm = () => {
    setFirstName(''); setSex('female'); setAge('adult'); setSymptoms([]); setExtra(''); setConditions([]);
  };

  const saveProfile = async () => {
    if (!firstName.trim()) { toast.error('Please enter a first name'); return; }
    setSaving(true);
    try {
      const created = await create({
        first_name: firstName.trim(), sex, age, symptoms, extra_symptoms: extra, conditions, is_family: false, member_ids: []
      });
      setActive(created.profile_id);
      toast.success(`${created.first_name}'s profile saved`);
      resetForm();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save profile');
    } finally {
      setSaving(false);
    }
  };

  const saveFamily = async () => {
    if (!familyName.trim()) { toast.error('Name your family profile'); return; }
    if (familyMembers.length < 2) { toast.error('Choose at least two profiles to combine'); return; }
    try {
      const created = await create({
        first_name: familyName.trim(), sex: 'any', age: 'adult', symptoms: [], extra_symptoms: '', conditions: [], is_family: true, member_ids: familyMembers
      });
      setActive(created.profile_id);
      toast.success('Family profile created');
      setFamilyName(''); setFamilyMembers([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create family profile');
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 py-10 min-h-[70vh]">
      <h1 className="font-serif text-4xl text-botanica-ink mb-1">Profiles</h1>
      <p className="text-botanica-muted mb-8">
        Create a profile so searches hide herbs, oils &amp; recipes that aren't safe for you.
        {isPremium ? ' You have unlimited profiles.' : ' Free members get 1 profile — upgrade for unlimited & family profiles.'}
      </p>

      {/* Existing profiles */}
      {profiles.length > 0 && (
        <div className="mb-10">
          <h2 className="font-serif text-xl text-botanica-ink mb-3">Your profiles</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {profiles.map((p) => (
              <div key={p.profile_id} className={`rounded-2xl p-4 border transition-colors ${activeId === p.profile_id ? 'border-botanica-green bg-botanica-greenLight' : 'border-botanica-card bg-botanica-card/50'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="w-9 h-9 rounded-xl bg-botanica-panel flex items-center justify-center">
                      {p.is_family ? <Users className="w-4 h-4 text-botanica-green" /> : <User className="w-4 h-4 text-botanica-green" />}
                    </span>
                    <div>
                      <p className="font-medium text-botanica-ink leading-tight">{p.first_name}</p>
                      <p className="text-xs text-botanica-muted capitalize">
                        {p.is_family ? `${p.member_ids?.length || 0} members` : `${p.sex} · ${p.age}`}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => remove(p.profile_id)} className="p-1.5 rounded-full text-botanica-muted hover:text-red-600 hover:bg-red-50 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {(p.conditions?.length > 0) && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {p.conditions.map((c) => (
                      <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-botanica-warn flex items-center gap-1">
                        <ShieldAlert className="w-3 h-3" /> {conditionLabel(c)}
                      </span>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => setActive(activeId === p.profile_id ? null : p.profile_id)}
                  className={`mt-3 w-full py-2 rounded-full text-xs font-medium transition-colors ${activeId === p.profile_id ? 'bg-botanica-green text-white' : 'bg-botanica-panel text-botanica-ink hover:bg-botanica-card'}`}
                >
                  {activeId === p.profile_id ? <span className="flex items-center justify-center gap-1"><Check className="w-3.5 h-3.5" /> Active</span> : 'Use this profile'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create new */}
      {atFreeLimit ? (
        <div className="rounded-3xl bg-botanica-greenLight p-7 text-center mb-8">
          <Crown className="w-7 h-7 text-botanica-green mx-auto mb-3" />
          <h3 className="font-serif text-xl text-botanica-ink mb-1">Want more profiles?</h3>
          <p className="text-sm text-botanica-greenDark mb-4">Free members get 1 profile. Upgrade to Premium for unlimited profiles and family profiles.</p>
          <button onClick={() => navigate('/membership')} className="px-6 py-2.5 rounded-full bg-botanica-green text-white font-medium hover:bg-botanica-greenDark transition-colors">
            Go Premium
          </button>
        </div>
      ) : (
        <div className="rounded-3xl bg-botanica-card/50 border border-botanica-card p-7 mb-8">
          <h2 className="font-serif text-xl text-botanica-ink mb-5 flex items-center gap-2"><Plus className="w-5 h-5 text-botanica-green" /> Create a profile</h2>

          <label className="block text-sm font-medium text-botanica-ink mb-1.5">First name</label>
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="e.g. Maya"
            className="w-full mb-5 px-4 py-3 rounded-xl bg-botanica-panel border border-botanica-card text-sm focus:outline-none focus:ring-2 focus:ring-botanica-green/40"
          />

          <label className="block text-sm font-medium text-botanica-ink mb-2">Sex</label>
          <div className="flex flex-wrap gap-2 mb-5">
            {sexOptions.map((o) => <Chip key={o.id} active={sex === o.id} onClick={() => setSex(o.id)}>{o.label}</Chip>)}
          </div>

          <label className="block text-sm font-medium text-botanica-ink mb-2">Age</label>
          <div className="flex flex-wrap gap-2 mb-5">
            {ageOptions.map((o) => <Chip key={o.id} active={age === o.id} onClick={() => setAge(o.id)}>{o.label}</Chip>)}
          </div>

          <label className="block text-sm font-medium text-botanica-ink mb-2">Symptoms you care about</label>
          <div className="flex flex-wrap gap-2 mb-5">
            {symptomOptions.map((s) => <Chip key={s} active={symptoms.includes(s)} onClick={() => toggle(symptoms, setSymptoms, s)}>{s}</Chip>)}
          </div>

          <label className="block text-sm font-medium text-botanica-ink mb-1.5">Additional symptoms</label>
          <textarea
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            placeholder="Anything else you'd like to note..."
            rows={2}
            className="w-full mb-5 px-4 py-3 rounded-xl bg-botanica-panel border border-botanica-card text-sm focus:outline-none focus:ring-2 focus:ring-botanica-green/40"
          />

          <label className="block text-sm font-medium text-botanica-ink mb-1">Medical conditions</label>
          <p className="text-xs text-botanica-muted mb-2">We'll hide anything unsafe for these. Select all that apply.</p>
          <div className="flex flex-wrap gap-2 mb-6">
            {conditionOptions.map((c) => <Chip key={c.id} active={conditions.includes(c.id)} onClick={() => toggle(conditions, setConditions, c.id)}>{c.label}</Chip>)}
          </div>

          <button
            onClick={saveProfile}
            disabled={saving}
            className="w-full py-3 rounded-full bg-botanica-green text-white font-medium hover:bg-botanica-greenDark transition-colors disabled:opacity-70"
          >
            Save profile
          </button>
        </div>
      )}

      {/* Family profile (premium) */}
      <div className="rounded-3xl bg-botanica-panel border border-botanica-card p-7">
        <h2 className="font-serif text-xl text-botanica-ink mb-1 flex items-center gap-2">
          <Users className="w-5 h-5 text-botanica-green" /> Family profile
          {!isPremium && <span className="text-[10px] uppercase tracking-widest text-botanica-green bg-botanica-greenLight px-2 py-0.5 rounded-full">Premium</span>}
        </h2>
        <p className="text-sm text-botanica-muted mb-4">Combine several profiles so searches stay safe for everyone in the family.</p>

        {!isPremium ? (
          <button onClick={() => navigate('/membership')} className="px-5 py-2.5 rounded-full bg-botanica-green text-white text-sm font-medium hover:bg-botanica-greenDark transition-colors">
            Upgrade to combine profiles
          </button>
        ) : profiles.filter((p) => !p.is_family).length < 2 ? (
          <p className="text-sm text-botanica-muted">Create at least two individual profiles first, then combine them here.</p>
        ) : (
          <>
            <input
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              placeholder="Family name, e.g. The Rivers Family"
              className="w-full mb-4 px-4 py-3 rounded-xl bg-botanica-card/50 border border-botanica-card text-sm focus:outline-none focus:ring-2 focus:ring-botanica-green/40"
            />
            <div className="flex flex-wrap gap-2 mb-4">
              {profiles.filter((p) => !p.is_family).map((p) => (
                <Chip key={p.profile_id} active={familyMembers.includes(p.profile_id)} onClick={() => toggle(familyMembers, setFamilyMembers, p.profile_id)}>
                  {p.first_name}
                </Chip>
              ))}
            </div>
            <button onClick={saveFamily} className="w-full py-3 rounded-full bg-botanica-green text-white font-medium hover:bg-botanica-greenDark transition-colors">
              Create family profile
            </button>
          </>
        )}
      </div>

      <p className="text-xs italic text-botanica-muted text-center mt-8">
        <Leaf className="w-3 h-3 inline mr-1 text-botanica-green" />
        Contraindication filtering is a helpful guide, not medical advice. Always confirm with a professional.
      </p>
    </div>
  );
}
